import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ordersApi, authFetch, apiUrl, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { P } from '../types/assistantPrivileges';
import OrdersAnalytics from '../components/orders/OrdersAnalytics';
import {
  buildOrderFilters,
  computeOrderAnalytics,
  fetchAllOrdersForAnalytics,
  type OrderAnalytics,
  type OrderRecord,
} from '../utils/orderAnalytics';
import {
  ORDER_STATUS_FILTER_OPTIONS,
  ORDER_TYPE_FILTER_OPTIONS,
  getOrderStatusBadgeClass,
  getOrderStatusLabel,
  getOrderTypeBadgeClass,
  getOrderTypeLabel,
  type OrderStatusName,
} from '../utils/orderTypes';

interface Teacher {
  id: string;
  fullName: string;
}

interface Order {
  id: string;
  studentId: string | null;
  studentName: string | null;
  studentPhoneNumber: string | null;
  courseId: string | null;
  courseName: string | null;
  unitId: number | null;
  unitName: string | null;
  type?: string | number | null;
  typeId?: number | null;
  teacherId?: string | null;
  teacherName?: string | null;
  promoCodeId: string | null;
  promoCode: string | null;
  originalPrice: number;
  discountAmount: number;
  netPrice: number;
  orderDate: string;
  status: OrderStatusName;
  paymentReference: string | null;
  paymentDate: string | null;
}

interface SortConfig {
  key: keyof Order;
  direction: 'asc' | 'desc';
}

const Orders: React.FC = () => {
  const { isSaeedAdmin, isAssistant, hasPrivilege } = useUser();
  const canViewOrders = isSaeedAdmin() || (isAssistant() && hasPrivilege(P.Order_View));
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [studentNameFilter, setStudentNameFilter] = useState<string>('');
  const [studentPhoneFilter, setStudentPhoneFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [promoCodeFilter, setPromoCodeFilter] = useState<string>('');
  const [orderIdFilter, setOrderIdFilter] = useState<string>('');
  const [teacherIdFilter, setTeacherIdFilter] = useState<string>('');
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [orderToUpdate, setOrderToUpdate] = useState<Order | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'analytics'>('list');
  const [analyticsOrders, setAnalyticsOrders] = useState<OrderRecord[]>([]);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const activeFilters = useMemo(
    () =>
      buildOrderFilters({
        orderIdFilter,
        statusFilter,
        typeFilter,
        studentNameFilter,
        studentPhoneFilter,
        promoCodeFilter,
        startDateFilter,
        endDateFilter,
        teacherIdFilter,
      }),
    [
      orderIdFilter,
      statusFilter,
      typeFilter,
      studentNameFilter,
      studentPhoneFilter,
      promoCodeFilter,
      startDateFilter,
      endDateFilter,
      teacherIdFilter,
    ]
  );

  const hasActiveFilters = activeFilters !== undefined;

  const orderAnalytics: OrderAnalytics = useMemo(
    () => computeOrderAnalytics(analyticsOrders),
    [analyticsOrders]
  );

  const fetchAnalytics = useCallback(async () => {
    if (!canViewOrders) return;

    setIsLoadingAnalytics(true);
    setAnalyticsError(null);
    try {
      const orders = await fetchAllOrdersForAnalytics(activeFilters);
      setAnalyticsOrders(orders);
    } catch (error) {
      console.error('Error fetching order analytics:', error);
      setAnalyticsError('فشل تحميل تحليلات الطلبات. يرجى المحاولة لاحقاً.');
      setAnalyticsOrders([]);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [activeFilters, canViewOrders]);

  // Fetch teachers for dropdown
  const fetchTeachers = useCallback(async () => {
    try {
      const response = await authFetch(
        apiUrl('Teacher'),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch teachers');
      const data = await response.json();
      
      if (data.success) {
        setTeachers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    }
  }, []);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    if (!canViewOrders) {
      setError('You do not have permission to view orders.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await ordersApi.getAllOrders(
        currentPage,
        itemsPerPage,
        activeFilters
      );

      if (response.success) {
        const orderData = response.data;
        setOrders(orderData.data || []);
        setTotalCount(orderData.totalCount || 0);
        setTotalPages(orderData.totalPages || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again later.');
      setOrders([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    canViewOrders,
    activeFilters,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (viewMode === 'analytics') {
      fetchAnalytics();
    }
  }, [viewMode, fetchAnalytics]);

  // Sorting logic
  const handleSort = (key: keyof Order) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting (filtering is now done on backend)
  const sortedOrders = useMemo(() => {
    let sortableOrders = [...orders];
    
    // Apply sorting
    if (sortConfig !== null) {
      sortableOrders.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableOrders;
  }, [orders, sortConfig]);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Handle status filter change
  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  const handleTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTypeFilter(e.target.value);
  };

  // Handle student name filter change
  const handleStudentNameFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStudentNameFilter(e.target.value);
  };

  // Handle student phone filter change
  const handleStudentPhoneFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStudentPhoneFilter(e.target.value);
  };

  // Handle start date filter change
  const handleStartDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateFilter(e.target.value);
  };

  // Handle end date filter change
  const handleEndDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDateFilter(e.target.value);
  };

  const handlePromoCodeFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCodeFilter(e.target.value);
  };

  const handleOrderIdFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderIdFilter(e.target.value);
  };

  const handleTeacherFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTeacherIdFilter(e.target.value);
  };

  // Apply filters
  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchOrders();
    if (viewMode === 'analytics') {
      fetchAnalytics();
    }
  };

  // Reset all filters
  const handleResetFilters = async () => {
    setStatusFilter('');
    setTypeFilter('');
    setStudentNameFilter('');
    setStudentPhoneFilter('');
    setPromoCodeFilter('');
    setOrderIdFilter('');
    setTeacherIdFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setCurrentPage(1);
    
    setLoading(true);
    setError(null);
    try {
      const response = await ordersApi.getAllOrders(1, itemsPerPage, undefined);
      
      if (response.success) {
        const orderData = response.data;
        setOrders(orderData.data || []);
        setTotalCount(orderData.totalCount || 0);
        setTotalPages(orderData.totalPages || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again later.');
      setOrders([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }

    if (viewMode === 'analytics') {
      setIsLoadingAnalytics(true);
      setAnalyticsError(null);
      try {
        const orders = await fetchAllOrdersForAnalytics(undefined);
        setAnalyticsOrders(orders);
      } catch (analyticsFetchError) {
        console.error('Error fetching order analytics:', analyticsFetchError);
        setAnalyticsError('فشل تحميل تحليلات الطلبات. يرجى المحاولة لاحقاً.');
        setAnalyticsOrders([]);
      } finally {
        setIsLoadingAnalytics(false);
      }
    }
  };

  // Handle status update
  const handleUpdateStatus = async (newStatus: OrderStatusName) => {
    if (!orderToUpdate) return;

    setIsUpdating(true);
    setError(null);
    try {
      const response = await ordersApi.updateOrderStatus(orderToUpdate.id, newStatus);
      
      if (response.success) {
        // Refresh orders list
        await fetchOrders();
        setOrderToUpdate(null);
      } else {
        throw new Error(response.message || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('Failed to update order status. Please try again later.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (!canViewOrders) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">ليس لديك صلاحية لعرض الطلبات.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">الطلبات</h1>
        <div className="inline-flex self-start rounded-2xl border border-gray-100 bg-white p-1.5 shadow-sm">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
              viewMode === 'list'
                ? 'bg-primary text-white shadow-md shadow-indigo-200'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            قائمة الطلبات
          </button>
          <button
            type="button"
            onClick={() => setViewMode('analytics')}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
              viewMode === 'analytics'
                ? 'bg-primary text-white shadow-md shadow-indigo-200'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            التحليلات
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {(error || analyticsError) && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="mr-3">
              <h3 className="text-sm font-medium text-red-800">خطأ</h3>
              <p className="mt-1 text-sm text-red-700">{error || analyticsError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">التصفية</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-8 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">order id</label>
            <input
              type="text"
              value={orderIdFilter}
              onChange={handleOrderIdFilterChange}
              placeholder="تصفية برقم الطلب..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">جميع الحالات</option>
              {ORDER_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">نوع الطلب</label>
            <select
              value={typeFilter}
              onChange={handleTypeFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">جميع الأنواع</option>
              {ORDER_TYPE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">المعلم</label>
            <select
              value={teacherIdFilter}
              onChange={handleTeacherFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">جميع المعلمين</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.fullName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">اسم الطالب</label>
            <input
              type="text"
              value={studentNameFilter}
              onChange={handleStudentNameFilterChange}
              placeholder="البحث بالاسم..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
            <input
              type="text"
              value={studentPhoneFilter}
              onChange={handleStudentPhoneFilterChange}
              placeholder="البحث برقم الهاتف..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">كود الخصم</label>
            <input
              type="text"
              value={promoCodeFilter}
              onChange={handlePromoCodeFilterChange}
              placeholder="مثال: SUMMER"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
            <input
              type="date"
              value={startDateFilter}
              onChange={handleStartDateFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={endDateFilter}
              onChange={handleEndDateFilterChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={handleResetFilters}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            إعادة تعيين الفلاتر
          </button>
          <button
            type="button"
            onClick={handleApplyFilters}
            className="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-sm hover:shadow-md flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            تطبيق الفلاتر
          </button>
        </div>
      </div>

      {viewMode === 'analytics' ? (
        <OrdersAnalytics
          analytics={orderAnalytics}
          isLoading={isLoadingAnalytics}
          hasActiveFilters={hasActiveFilters}
        />
      ) : (
      <>
      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <span className="font-medium">الإجمالي: {totalCount}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { key: 'id' as const, label: 'رقم الطلب' },
                  { key: 'studentName' as const, label: 'اسم الطالب' },
                  { key: 'studentPhoneNumber' as const, label: 'رقم الهاتف' },
                  { key: 'courseName' as const, label: 'اسم الدورة' },
                  { key: 'unitName' as const, label: 'اسم الوحدة' },
                  { key: 'teacherName' as const, label: 'المعلم' },
                  { key: 'type' as const, label: 'نوع الطلب' },
                  { key: 'promoCode' as const, label: 'كود الخصم' },
                  { key: 'originalPrice' as const, label: 'السعر الأصلي' },
                  { key: 'discountAmount' as const, label: 'مبلغ الخصم' },
                  { key: 'netPrice' as const, label: 'السعر النهائي' },
                  { key: 'orderDate' as const, label: 'تاريخ الطلب' },
                  { key: 'status' as const, label: 'الحالة' },
                  { key: 'paymentReference' as const, label: 'مرجع الدفع' },
                  { key: 'paymentDate' as const, label: 'تاريخ الدفع' },
                  { key: 'actions' as const, label: 'الإجراءات' }
                ].map(({ key, label }) => {
                  const isActions = key === 'actions';
                  const isTypeColumn = key === 'type';
                  return (
                    <th
                      key={key}
                      onClick={() => !isActions && !isTypeColumn && handleSort(key as keyof Order)}
                      className={`px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        !isActions && !isTypeColumn ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                      }`}
                    >
                      {label}
                      {sortConfig?.key === key && !isActions && !isTypeColumn && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={16} className="px-6 py-12">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedOrders.length === 0 ? (
                <tr>
                  <td colSpan={16} className="px-6 py-4 text-center text-gray-500">
                    لا توجد طلبات
                  </td>
                </tr>
              ) : (
                sortedOrders.map((order: Order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-sm">
                      {order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.studentName || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.studentPhoneNumber || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.courseName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.unitName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.teacherName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${getOrderTypeBadgeClass(order)}`}>
                        {getOrderTypeLabel(order)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {order.promoCode || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(order.originalPrice)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(order.discountAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      {formatCurrency(order.netPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(order.orderDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getOrderStatusBadgeClass(order.status)}`}>
                        {getOrderStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {order.paymentReference || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatDate(order.paymentDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setOrderToUpdate(order)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        تحديث الحالة
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 0 && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-gray-700">
                عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalCount)}
                </span>{' '}
                من <span className="font-medium">{totalCount}</span> نتيجة
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">الأول</span>
                  <span>&laquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">السابق</span>
                  <span>&lsaquo;</span>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    if (index > 0 && prevPage !== page - 1) {
                      return [
                        <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>,
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                            currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ];
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                          currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }).flat()}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">التالي</span>
                  <span>&rsaquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">الأخير</span>
                  <span>&raquo;</span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* Update Status Modal */}
      {orderToUpdate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200">
            <div className="text-center mb-5">
              <h3 className="text-xl font-medium text-gray-900 mb-2">تحديث حالة الطلب</h3>
              <p className="text-gray-600">
                الطلب: {orderToUpdate.id}<br />
                الحالة الحالية: {getOrderStatusLabel(orderToUpdate.status)}
              </p>
            </div>
            <div className="mt-6 space-y-3">
              {ORDER_STATUS_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleUpdateStatus(option.value)}
                  disabled={isUpdating || orderToUpdate.status === option.value}
                  className={`w-full px-4 py-2 rounded-lg text-white font-medium transition-all disabled:opacity-50 ${
                    orderToUpdate.status === option.value
                      ? 'bg-gray-400 cursor-not-allowed'
                      : option.value === 'Pending'
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : option.value === 'Completed'
                      ? 'bg-green-600 hover:bg-green-700'
                      : option.value === 'Cancelled'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setOrderToUpdate(null)}
                disabled={isUpdating}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
